// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import { put, select, takeLatest, race, delay, take, getContext } from "redux-saga/effects";
import { Store } from "@reduxjs/toolkit";

import {
  ActionType,
  raiseAction,
  IAction,
  IActionTypeSetPeriod,
  ActionGraphCommand,
  IActionCreateReport,
} from "actions";
import { IState } from "states/states";
import { IStateScheduler, ISchedulerItem, AppState } from "states/schedulerstates";
import { DataState, MainPanel, RootPath, TimePeriod } from "types";
import TrendMonitorModel from "model/trendmonitor";
import { isSet } from "shared/utils/utils";
import { getSiteSelection, hasLocalSettingsPage, isLocalhost, isOcppFrontend } from "guiutils";
import { createBrowserRouter } from "react-router-dom";
import { IStateRouter } from "states/routerstates";
import { ApiSagas } from "./apisagas";

// uses webpack definitions
declare const IS_CLOUD: boolean;

const BASE_POLL_REOCCURANCE = 1000 * 3600 * 1; // 1 hour poll interval for basis configuration
const BASE_POLL_RETRY_DELAY = 1000 * 15; // 15 s timeout for base poll timeouts
const TREND_POLL_RETRY_DELAY = 1000 * 25; // 20 s timeout for trend polls in lambda timeout, a bit above
const DATA_POLL_REOCCURANCE_LINX = 1000 * 2; // 2 s data refresh interval
const DATA_POLL_REOCCURANCE_LINX_LONG = 1000 * 10; // 10 s longer data refresh interval, e.g. for alarms
const DATA_POLL_REOCCURANCE_CLOUD = 1000 * 60; // 60 s data refresh interval for cloud client
const DATA_POLL_REOCCURANCE_BUSY = 100; // 100 ms data refresh interval when busy, i.e. trend loading
const LIVE_DATA_REGISTER_INTERVAL = 1000 * 60 * 2; // every 2nd minutes, we register for live data; LINX timeout is 900 s, ocpp-stack timeout is 300 s

// main entry point to load sagas at startup
function* mySaga(store: Store, apiSagas: ApiSagas) {
  if (!apiSagas) {
    console.error("error loading sagas");
    return;
  }

  // load communication interface and register sagas
  yield apiSagas.initApi(store);

  // register api sagas
  yield* apiSagas.initSagas();

  // ensure immediate reaction on receiving relevant new data
  yield takeLatest(
    [
      // login event allows site loading
      ActionType[ActionType.ACT_LOGIN_SUCC],
      // site/user list allows auto-site selection
      ActionType[ActionType.ACT_GET_SITES_SUCC],
      ActionType[ActionType.ACT_GET_SITES_FAIL],
      ActionType[ActionType.ACT_GET_USERLIST_BASIC_SUCC],
      ActionType[ActionType.ACT_GET_USERLIST_BASIC_FAIL],
      // site selection allows for parameter loading
      ActionType[ActionType.ACT_SELECT_SITE],
      // parameter/charger fails allow for retrials
      ActionType[ActionType.ACT_GET_PARAMETER_FAIL],
      ActionType[ActionType.ACT_GET_OCPP_CHARGERS_FAIL],
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_BASIC_FAIL],
      ActionType[ActionType.ACT_GET_OCPP_SITE_TARIFFS_FAIL],
      ActionType[ActionType.ACT_GET_PENDING_OCPP_SESSIONS_FAIL],
      // parameters done allow for subscribing live data
      ActionType[ActionType.ACT_PARAMETER_LOADED],
      // immediate trend loading at request
      ActionType[ActionType.ACT_UPDATE_TREND],
      ActionType[ActionType.ACT_UPDATE_IOTREND],
      // wake-up after long sleep
      ActionType[ActionType.ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT],
    ],
    function* () {
      yield put(raiseAction(ActionType.ACT_AWAKE));
    },
  );

  // loaded parameters?
  yield takeLatest(
    [
      ActionType[ActionType.ACT_GET_PARAMETER_SUCC],
      ActionType[ActionType.ACT_GET_OCPP_CHARGERS_SUCC],
      ActionType[ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC],
      ActionType[ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC],
      ActionType[ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC],
      ActionType[ActionType.ACT_LOGIN_SUCC],
    ],
    function* () {
      const state = (yield select(getState)) as IState;
      // check for finish
      if (state.scheduler.appState < AppState.PARAMETER_LOADED && isParameterDataComplete(state)) {
        yield put(raiseAction(ActionType.ACT_PARAMETER_LOADED));
      }
    },
  );

  // switch to idle state when live data first received
  // but do this only if a state change is still needed
  yield takeLatest(
    [
      ActionType[ActionType.ACT_GET_ALLDATA_SUCC],
      ActionType[ActionType.ACT_GET_CLOUDLIVEDATA_SUCC],
      ActionType[ActionType.ACT_GET_ALLDATA_FAIL],
    ],
    function* () {
      const schedulerState = (yield select(getSchedulerState)) as IStateScheduler;
      if (schedulerState.appState < AppState.READY) {
        yield put(raiseAction(ActionType.ACT_AWAKE));
      }
    },
  );

  // scheduler events
  yield takeLatest(ActionType[ActionType.ACT_POLL_SCHEDULE_TICK], pollTick);
  yield takeLatest(ActionType[ActionType.ACT_INIT_DONE], initDone);

  // GUI events
  yield takeLatest(ActionType[ActionType.ACT_NAVIGATION], handleNavigation);

  // everything set up
  yield put(raiseAction(ActionType.ACT_APP_LOADED));

  // check for automatic login via url parameter or session storage
  yield apiSagas.doAutoLogin();

  // start polling, even if we cannot do much yet as potentially without login
  yield put(raiseAction(ActionType.ACT_POLL_SCHEDULE_TICK));
}
export default mySaga;

const getState = (state: IState) => state;
const getSchedulerState = (state: IState) => state.scheduler;
const getGuiState = (state: IState) => state.gui;

function* initDone() {
  const state = (yield select(getState)) as IState;
  // route to default panel, if no panel is selected yet or we just come from site selection/logout
  // also, inhibit forbidden access to local controller settings
  if (!isSet(state.router.panel) || state.router.panel === MainPanel.SITES) {
    // determine starting panel
    const newPanel =
      isOcppFrontend(state.router.rootPath) ? MainPanel.CHARGERLIST
      : state.router.rootPath === RootPath.TREND ? MainPanel.ENERGY
      : !IS_CLOUD && isLocalhost() ? MainPanel.LOCAL_SETTINGS
      : state.site.user?.isRfidUser === true ? MainPanel.CHARGINGSESSIONLIST
      : state.scheduler.allData.lastData ? MainPanel.DASHBOARD
      : state.para.parameter?.tariffline.length ? MainPanel.ENERGY
      : state.ocpp.charger.length > 0 ? MainPanel.CHARGERLIST
      : undefined;
    if (isSet(newPanel)) {
      const router: ReturnType<typeof createBrowserRouter> = yield getContext("router");
      if (state.router.rootPath) {
        router.navigate(`/${state.router.rootPath}/${newPanel}`);
      } else {
        router.navigate(`/${newPanel}`);
      }
    }
  } else {
    // no navigation, keep on existing side
    // still, we might need to load data here
    yield handleNavigation(
      raiseAction(ActionType.ACT_NAVIGATION, state.router) as IAction<IStateRouter>,
    );
  }
}

function* handleNavigation(action: IAction<IStateRouter>) {
  const newPanel = action.payload.panel;
  const state = (yield select(getState)) as IState;
  // ensure that we start at top of page
  window.scrollTo(0, 0);

  // based on the requested web page, load appropriate data
  // we may switch from switching activities to trend tab, facing a new date that needs to be loaded
  if (newPanel === MainPanel.ENERGY) {
    yield put(raiseAction(ActionType.ACT_UPDATE_TREND));
  }

  // when navigating to dashboard, ensure to have trend data of current date
  if (newPanel === MainPanel.DASHBOARD) {
    if (isSet(state.gui.trendPeriod.selectedDate)) {
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.END,
      };
      yield put(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
    }
    yield put(raiseAction(ActionType.ACT_UPDATE_TREND));
  }
  // when navigating to custom dashboard, ensure to have trend data of requested period
  if ([MainPanel.CUSTOM_DASHBOARD, MainPanel.PUBLIC_DISPLAY].includes(newPanel!)) {
    yield put(raiseAction(ActionType.ACT_UPDATE_TREND));
  }

  if (newPanel === MainPanel.TRENDMONITOR) {
    const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
    const siteSelection = getSiteSelection(state);
    const idxs = TrendMonitorModel.getActiveSortedTrendMonitorIdxs(
      site?.cloudSettings?.trendMonitor ?? [],
    );
    const selectedMonitor = siteSelection?.trendMonitor ?? idxs[0];
    const trendMonitor =
      isSet(selectedMonitor) ? site?.cloudSettings?.trendMonitor?.[selectedMonitor] : undefined;

    if (trendMonitor && state.gui.trendPeriod.timePeriod !== (trendMonitor.period as unknown)) {
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        timePeriod: trendMonitor.period as unknown as TimePeriod,
        selectedDate: Date.now() / 1000,
      };
      yield put(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
    }
    yield put(raiseAction(ActionType.ACT_UPDATE_TREND));
  }

  // ensure initial load of io trend data
  if (newPanel === MainPanel.IO_TREND) {
    // trigger an io trend load
    yield put(raiseAction(ActionType.ACT_UPDATE_IOTREND));
  }

  // select proper charging session
  if (newPanel === MainPanel.CHARGINGSESSION) {
    let transactionId = state.router.transaction;
    if (!transactionId) {
      // load first pending session
      const state = (yield select(getState)) as IState;
      transactionId = state.ocpp.pendingChargingSession[0]?.id;
    }
    yield put(raiseAction(ActionType.ACT_SELECT_CHARGING_SESSION, transactionId));
  }
  if (
    [
      MainPanel.CHARGERLIST,
      MainPanel.CHARGERDETAIL,
      MainPanel.RFIDLIST,
      MainPanel.RFIDDETAIL,
      MainPanel.CHARGINGSESSIONLIST,
    ].includes(newPanel!)
  ) {
    // load charging session list
    yield put(raiseAction(ActionType.ACT_GET_OCPP_SESSIONS_REQ));
  }

  // ensure information on ocpp settings page
  if (newPanel === MainPanel.OCPPCONFIGURATION) {
    // load ocpp configuration data
    yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
    yield put(raiseAction(ActionType.ACT_GET_OCPP_REFUSED_RFIDS_REQ));
  }
  // in RFID list/detail, we show the last usage
  if ([MainPanel.RFIDDETAIL].includes(newPanel!)) {
    yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ));
  }

  // ensure information on user settings page
  if (newPanel === MainPanel.USER_SETTINGS) {
    // without a selected user the page shows the logged-in user
    const userId =
      action.payload.user ??
      state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId)?.id;
    // load extended user data
    yield put(raiseAction(ActionType.ACT_GET_USERLIST_EXT_REQ, userId));
    // RFID users do not have alarm notifications
    if (!state.site.user?.isRfidUser) {
      yield put(raiseAction(ActionType.ACT_GET_ALARM_NOTIFICATIONS_REQ, userId));
    }
  }
  // ensure information on site settings page
  if (state.router.panel === MainPanel.SITE_SETTINGS) {
    yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_REQ));
  }
  // ensure information on site parameters page
  if (newPanel === MainPanel.SITE_PARAMETERS) {
    yield put(raiseAction(ActionType.ACT_GET_PARAMETER_HISTORY_REQ));
  }

  // trigger report generation
  if (newPanel === MainPanel.REPORTDATAVIEW) {
    const state = (yield select(getState)) as IState;
    const reportId = state.router.report;
    if (isSet(reportId)) {
      const payload: IActionCreateReport = {
        reportId,
        previewOnly: true,
      };
      yield put(raiseAction(ActionType.ACT_CREATE_REPORT_REQ, payload));
    } else {
      console.error("missing report id in url");
    }
  }
}

function isRequestOutdated(schedulerItem: ISchedulerItem, timeoutMs: number): boolean {
  const isReqOutdated =
    !isSet(schedulerItem.lastRequest) || Date.now() - schedulerItem.lastRequest > timeoutMs;
  return isReqOutdated;
}
// trend polls need a higher timeout
function isTrendRequestOutdated(schedulerItem: ISchedulerItem): boolean {
  const isReqOutdated =
    !isSet(schedulerItem.lastRequest) ||
    Date.now() - schedulerItem.lastRequest > TREND_POLL_RETRY_DELAY;
  return isReqOutdated;
}
function isBaseOutdated(schedulerItem: ISchedulerItem): boolean {
  const isDataCurrent =
    isSet(schedulerItem.lastData) && Date.now() - schedulerItem.lastData < BASE_POLL_REOCCURANCE;
  const isNoReqPending =
    isRequestOutdated(schedulerItem, BASE_POLL_RETRY_DELAY) ||
    (schedulerItem.lastData ?? 0) > (schedulerItem.lastRequest ?? 0);
  return !isDataCurrent && isNoReqPending;
}
function isDataOutdated(schedulerItem: ISchedulerItem, longerTimeout?: boolean): boolean {
  const timeout = longerTimeout ? DATA_POLL_REOCCURANCE_LINX_LONG : DATA_POLL_REOCCURANCE_LINX;
  const isDataCurrent =
    isSet(schedulerItem.lastData) && Date.now() - schedulerItem.lastData < timeout;
  const isNoReqPending =
    !isSet(schedulerItem.lastRequest) ||
    (schedulerItem.lastData ?? 0) > (schedulerItem.lastRequest ?? 0) ||
    Date.now() - schedulerItem.lastRequest > timeout;
  return !isDataCurrent && isNoReqPending;
}

function isParameterDataNeeded(state: IState) {
  return !isOcppFrontend(state.router.rootPath) && !(state.site.user?.isRfidUser === true);
}

// keep the checks here consistent with the actions in sagas state SITE_SELECTED
function isParameterDataComplete(state: IState) {
  const schedulerState = state.scheduler;
  return (
    schedulerState.appState >= AppState.SITE_SELECTED &&
    (!isParameterDataNeeded(state) || isSet(schedulerState.parameter.lastData)) &&
    (!IS_CLOUD || isSet(schedulerState.ocppChargerList.lastData)) &&
    (!IS_CLOUD || isSet(schedulerState.ocppRfidListBasic.lastData)) &&
    (!IS_CLOUD || isSet(schedulerState.ocppTariffList.lastData)) &&
    (!IS_CLOUD || isSet(schedulerState.pendingOcppSessionState.lastData))
  );
}

function isLiveDataComplete(state: IState) {
  const schedulerState = state.scheduler;

  // we do not need live data for
  // - restricted accesses (ocpp, trend) or
  // - virtual sites, e.g. as an pure ocpp site
  const isLiveDataNeeded =
    !isOcppFrontend(state.router.rootPath) &&
    !(state.router.rootPath === RootPath.TREND) &&
    isSet(state.para.parameter) &&
    (state.para.parameter?.input.length ||
      state.para.parameter?.energymeter.length ||
      state.para.parameter?.tariffline.length ||
      state.para.parameter?.output.length ||
      state.para.parameter?.charger.length);

  return (
    schedulerState.appState >= AppState.PARAMETER_LOADED &&
    (!isLiveDataNeeded || isSet(schedulerState.allData.lastData))
  );
}

function getTaskTimer(schedulerState: IStateScheduler) {
  // derive delay until next status poll
  let pollDelay = IS_CLOUD ? DATA_POLL_REOCCURANCE_CLOUD : DATA_POLL_REOCCURANCE_LINX;

  if (schedulerState.trendLoadingState === DataState.SAVE_REQ) {
    // faster LINX polling while loading trend data even in cloud
    pollDelay = DATA_POLL_REOCCURANCE_LINX;
  }

  if (schedulerState.liveViaProxy) {
    // poll the controller live data via the proxy at the fast LINX rate
    pollDelay = DATA_POLL_REOCCURANCE_LINX;
  }

  if (schedulerState.appState < AppState.LIVE_DATA_SUBSCRIBED) {
    // slower polling while waiting for base data
    pollDelay = BASE_POLL_RETRY_DELAY;
  }

  return pollDelay;
}

/*
 This is the main working thread of an initialized application.
 It gets initially triggered at startup and schedules itself repeatedly.
*/
function* pollTick(action: IAction<undefined>) {
  let state = (yield select(getState)) as IState;
  let schedulerState = state.scheduler;

  // check for required data loading
  switch (schedulerState.appState) {
    case AppState.INIT:
      console.error("unexpected init app state");
      break;

    case AppState.LOGGED_OUT:
      // load device info / serial number on local web interface
      if (!IS_CLOUD && !isSet(state.controller?.serialNumber)) {
        yield put(raiseAction(ActionType.ACT_GET_DEVICEINFO_REQ));
      }
      // otherwise, nothing to do; wait for user login
      break;

    case AppState.LOGGED_IN:
      if (IS_CLOUD) {
        // no need to load site list for RFID users
        if (isBaseOutdated(schedulerState.siteList) && !state.site.user?.isRfidUser) {
          yield put(raiseAction(ActionType.ACT_GET_SITES_REQ));
        }
        if (isBaseOutdated(schedulerState.userListBasic)) {
          yield put(raiseAction(ActionType.ACT_GET_USERLIST_BASIC_REQ));
        }
        // no need to load company list for RFID users
        if (isBaseOutdated(schedulerState.companyList) && !state.site.user?.isRfidUser) {
          yield put(raiseAction(ActionType.ACT_GET_COMPANIES_REQ));
        }
      } else {
        console.error("unexpected login app state");
      }
      break;

    case AppState.SITE_LIST_LOADED:
      // auto-select site if there is only a single entry
      if (state.site.siteList?.length === 1) {
        yield put(raiseAction(ActionType.ACT_SELECT_SITE, state.site.siteList[0].id));
      }
      // auto-select site if we already have a valid selection
      else if (state.site.siteList?.some((curItem) => curItem.id === state.cached.selectedSite)) {
        // this will reselect the cached site
        yield put(raiseAction(ActionType.ACT_SELECT_SITE));
      }
      // otherwise, wait for user selection of site
      break;

    case AppState.SITE_SELECTED:
      yield handleBaseRequests(state);
      break;

    case AppState.PARAMETER_LOADED:
      // this is executed exactly once
      if (IS_CLOUD) {
        // register for graphql live data pushes
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ, true));

        // this has no retries on error
        const hasChargers = state.ocpp.charger.length > 0;
        if (hasChargers) {
          // load time boundaries of charging sessions
          yield put(raiseAction(ActionType.ACT_GET_OCPP_TRENDINFO_REQ));
          // load initial ocpp connector data
          yield put(raiseAction(ActionType.ACT_GET_OCPP_CONNECTORS_REQ));
        }
      } else {
        // initial read of live data directly from LINX
        // anyway, live data are also read during normal polling
        yield put(raiseAction(ActionType.ACT_GET_ALLDATA_REQ));
        // initial read of alarm data
        // anyway, alarms are also read during normal polling
        yield put(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
      }

      // load trend time boundaries and initial trend data
      // this has no retries on error
      if (!isOcppFrontend(state.router.rootPath) && !state.site.user?.isRfidUser) {
        yield put(raiseAction(ActionType.ACT_GET_TRENDINFO_REQ));
      }

      // switch state after this one-time action
      // we might need to wait for live data receiption or not
      if (isLiveDataComplete(state)) {
        yield put(raiseAction(ActionType.ACT_INIT_DONE));
      } else {
        yield put(raiseAction(ActionType.ACT_LIVE_DATA_SUBSCRIBED));
      }
      break;

    case AppState.LIVE_DATA_SUBSCRIBED:
      // nothing to do here; we wait until we receive live data
      if (isLiveDataComplete(state)) {
        // we just finished
        yield put(raiseAction(ActionType.ACT_INIT_DONE));
        // do a first-time handling of idle data immediately, update the new appState before
        state = (yield select(getState)) as IState;
        yield handleIdleState(state);
      }
      break;

    case AppState.READY:
      // main working state
      yield handleIdleState(state);
      break;

    default:
      console.error("unhandled app state", schedulerState.appState);
  }

  // loading local controller data is independent on application state
  if (
    schedulerState.appState !== AppState.INIT &&
    (hasLocalSettingsPage(state.controller) || state.router.panel === MainPanel.LOCAL_SETTINGS) &&
    isDataOutdated(schedulerState.controllerData, true)
  ) {
    yield put(raiseAction(ActionType.ACT_GET_CONTROLLERDATA_REQ));
  }

  // reschedule main task and sleep until next invocation
  const taskTimer = getTaskTimer(schedulerState);
  const { task, cancel } = yield race({
    task: delay(taskTimer),
    cancel: take(ActionType[ActionType.ACT_AWAKE]),
  });

  // normal task time out?
  if (task) {
    // we need to re-get the scheduler state
    schedulerState = (yield select(getSchedulerState)) as IStateScheduler;
    // we abort the init sequence after a timeout waiting for live data
    if (schedulerState.appState === AppState.LIVE_DATA_SUBSCRIBED) {
      console.log("base init timed out");
      yield put(raiseAction(ActionType.ACT_INIT_DONE));
    }
  }
  // got wake-up signal?
  if (cancel) {
    // ensure short delay, even here
    yield delay(DATA_POLL_REOCCURANCE_BUSY);
  }

  // start again main schedule loop
  yield put(raiseAction(ActionType.ACT_POLL_SCHEDULE_TICK));
}

function* handleBaseRequests(state: IState) {
  const schedulerState = state.scheduler;
  // load parameter data
  if (isParameterDataNeeded(state) && isBaseOutdated(schedulerState.parameter)) {
    yield put(raiseAction(ActionType.ACT_GET_PARAMETER_REQ));
  }
  if (IS_CLOUD) {
    // reports belong to the user, RFID users have none
    if (isBaseOutdated(schedulerState.reportList) && !state.site.user?.isRfidUser) {
      yield put(raiseAction(ActionType.ACT_GET_REPORTS_REQ));
    }
    if (isBaseOutdated(schedulerState.ocppChargerList)) {
      yield put(raiseAction(ActionType.ACT_GET_OCPP_CHARGERS_REQ));
    }
    if (isBaseOutdated(schedulerState.ocppRfidListBasic)) {
      yield put(raiseAction(ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ));
    }
    if (isBaseOutdated(state.scheduler.ocppTariffList)) {
      yield put(raiseAction(ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ));
    }
    if (isBaseOutdated(schedulerState.pendingOcppSessionState)) {
      yield put(raiseAction(ActionType.ACT_GET_PENDING_OCPP_SESSIONS_REQ));
    }
  }
}

function* handleIdleState(state: IState) {
  const schedulerState = state.scheduler;

  // check for specific cloud/LINx polling actions
  // these are only needed if the page is actually visible
  if (!schedulerState.isInBackground) {
    if (IS_CLOUD) {
      // inform LINX via cloud that we are still interested in getting live data
      // this also keeps the proxy token authorized; applicable for RFID users too
      if (isRequestOutdated(schedulerState.pushRegister, LIVE_DATA_REGISTER_INTERVAL)) {
        // re-register for live data here to keep receiving them
        yield put(raiseAction(ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ, false));
      }
    }
    // live data polling?
    if ((schedulerState.liveViaProxy || !IS_CLOUD) && isDataOutdated(schedulerState.allData)) {
      yield put(raiseAction(ActionType.ACT_GET_ALLDATA_REQ));
    }

    // alarm are read repeated on LINX but only once for cloud client
    if (
      !state.site.user?.isRfidUser &&
      isDataOutdated(schedulerState.alarm, true) &&
      (!IS_CLOUD || !isSet(schedulerState.alarm.lastData))
    ) {
      yield put(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
    }

    if (
      schedulerState.isFileUploadLoading &&
      isRequestOutdated(schedulerState.fileUploadLoading, DATA_POLL_REOCCURANCE_CLOUD)
    ) {
      yield put(raiseAction(ActionType.ACT_GET_FILEUPLOADS_REQ));
    }
  }

  // no outdated check here, the schedule tick is set by this anyway
  if (
    schedulerState.trendLoadingState === DataState.SAVE_REQ &&
    isTrendRequestOutdated(schedulerState.trendLoading)
  ) {
    yield put(raiseAction(ActionType.ACT_GET_TREND_REQ));
  }
  if (schedulerState.isIoTrendLoading && isTrendRequestOutdated(schedulerState.ioTrendLoading)) {
    yield put(raiseAction(ActionType.ACT_GET_IOTREND_REQ));
  }

  // check for base data refreshs
  yield handleBaseRequests(state);
}
